import { Settings, AlertTriangle } from "lucide-react";
import { useConfigStore } from "../../../store/configStore";
import { useEditorStore } from "../../../store/editorStore";
import { UploadArea } from "../components/UploadArea";
import { parseSpineToConfig, readFileAsText } from "../../../lib/spineParser";
import { ImportedTemplateConfig } from "../types";

interface TemplateUploaderProps {
  onSuccess: () => void;
}

export function TemplateUploader({ onSuccess }: TemplateUploaderProps) {
  const { spineData, templateConfig, setTemplateConfig } = useConfigStore();
  const { randomizeSkins } = useEditorStore();

  const handleImportTemplate = async (file: File) => {
    try {
      const templateText = await readFileAsText(file);
      const importedTemplate = JSON.parse(templateText) as ImportedTemplateConfig;

      if (!importedTemplate.selections && !importedTemplate.tonings) {
        alert("无效的模板配置文件");
        return;
      }

      if (!spineData) {
        alert("请先导入 Spine 文件");
        return;
      }

      const baseConfig = parseSpineToConfig(spineData.json);

      // 处理导入的配置
      const skus: Record<string, any> = {};
      const processedSelection = (importedTemplate.selections || []).map(
        (sel: any) => {
          const skuIds: string[] = [];
          // 处理 SKU 数据
          for (const sku of sel.skus || []) {
            skuIds.push(sku.id);
            // 存储完整 SKU 到 skus
            skus[sku.id] = {
              id: sku.id,
              skinName: sku.skinName,
              category: sku.category,
              ...(sku.pro && { pro: sku.pro }),
              ...(sku.hidden && { hidden: true }),
              ...(sku.toningIds?.length > 0 && {
                toningIds: sku.toningIds,
              }),
              ...(sku.originalAssetId && { originalAssetId: sku.originalAssetId }),
              ...(sku.thumbAssetId && { thumbAssetId: sku.thumbAssetId }),
            };
          }
          return {
            id: sel.id,
            playIndex: sel.playIndex,
            name: sel.name,
            ...(sel.cover && { layerIconId: sel.cover }),
            ...(sel.hidden && { hidden: true }),
            ...(typeof sel.opt === "boolean" && { opt: sel.opt }),
            skuIds,
          };
        },
      );

      const newConfig = {
        selection: processedSelection,
        tonings: importedTemplate.tonings || [],
        animations: importedTemplate.animations || baseConfig.animations,
        requireCategories: importedTemplate.requireCategories || [],
        skus,
      };

      setTemplateConfig(newConfig);

      // 随机换装
      setTimeout(() => {
        randomizeSkins();
      }, 0);

      onSuccess();
    } catch (err) {
      alert(`导入失败: ${err instanceof Error ? err.message : "未知错误"}`);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col h-full">
      {templateConfig && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 shrink-0">
          <div className="flex gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">
              导入模板配置将覆盖现有的 Selections、Tonings 和 SKUs 配置，动画列表将从当前 Spine 数据中获取。
            </p>
          </div>
        </div>
      )}
      <UploadArea
        icon={<Settings className="h-12 w-12 mx-auto mb-4 text-purple-500" />}
        description="拖拽文件到此处上传"
        fileHint="支持 .json 文件（模板配置文件）"
        accept={{ "application/json": [".json"] }}
        onDrop={(files) => {
          const fileList = Array.from(files);
          const jsonFile = fileList.find((f) => f.name.toLowerCase().endsWith(".json"));
          if (jsonFile) {
            handleImportTemplate(jsonFile);
          } else {
            alert("请拖拽 .json 文件");
          }
        }}
      />
    </div>
  );
}
